import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Eye, ArrowLeft, PenTool, Layers, Home, Info, Zap, GraduationCap, MapPin, DollarSign, Briefcase } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import RichTextEditor from '../../components/admin/RichTextEditor';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

const PageEditor: React.FC = () => {
  const { page } = useParams<{ page: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageData, setPageData] = useState({
    id: '',
    title: '',
    slug: '',
    description: '',
    sections: [] as { id: string; title: string; content: string; order: number }[]
  });

  useEffect(() => {
    document.title = `Éditer la Page ${getPageTitle()} | Admin`;
    loadPageData();
  }, [page]);

  const getPageTitle = () => {
    switch (page) {
      case 'home':
        return 'Accueil';
      case 'about':
        return 'À Propos';
      case 'services':
        return 'Services';
      case 'training':
        return 'Formation';
      case 'stations':
        return 'Stations';
      case 'proxy-power':
        return 'Proxy Power';
      case 'invest':
        return 'Investir';
      case 'careers':
        return 'Carrières';
      default:
        return page || '';
    }
  };

  const getPageIcon = () => {
    switch (page) {
      case 'home':
        return <Home size={20} />;
      case 'about':
        return <Info size={20} />;
      case 'services':
        return <Zap size={20} />;
      case 'training':
        return <GraduationCap size={20} />;
      case 'stations':
        return <MapPin size={20} />;
      case 'proxy-power':
        return <Layers size={20} />;
      case 'invest':
        return <DollarSign size={20} />;
      case 'careers':
        return <Briefcase size={20} />;
      default:
        return <PenTool size={20} />;
    }
  };

  const loadPageData = async () => {
    try {
      setLoading(true);
      
      // Check if page exists in database
      const { data: pageData, error: pageError } = await supabase
        .from('pages')
        .select('*')
        .eq('slug', page)
        .single();
      
      if (pageError && pageError.code !== 'PGRST116') {
        throw pageError;
      }
      
      let pageId = '';
      
      // If page doesn't exist, create it
      if (!pageData) {
        const { data: newPage, error: createError } = await supabase
          .from('pages')
          .insert([{
            title: getPageTitle(),
            slug: page || '',
            description: `Page ${getPageTitle()}`
          }])
          .select()
          .single();
        
        if (createError) throw createError;
        
        if (newPage) {
          pageId = newPage.id;
          setPageData({
            id: newPage.id,
            title: newPage.title,
            slug: newPage.slug,
            description: newPage.description || '',
            sections: []
          });
        }
      } else {
        pageId = pageData.id;
        setPageData({
          id: pageData.id,
          title: pageData.title,
          slug: pageData.slug,
          description: pageData.description || '',
          sections: []
        });
      }
      
      // Load page sections
      if (pageId) {
        const { data: sections, error: sectionsError } = await supabase
          .from('page_sections')
          .select('*')
          .eq('page_id', pageId)
          .order('order', { ascending: true });
        
        if (sectionsError) throw sectionsError;
        
        if (sections && sections.length > 0) {
          setPageData(prev => ({
            ...prev,
            sections: sections.map(section => ({
              id: section.id,
              title: section.title,
              content: section.content,
              order: section.order
            }))
          }));
        } else {
          // Create default sections if none exist
          const defaultSections = [
            { title: 'Introduction', content: '<p>Contenu de l\'introduction</p>', order: 1 },
            { title: 'Section Principale', content: '<p>Contenu principal</p>', order: 2 }
          ];
          
          for (const section of defaultSections) {
            const { error: insertError } = await supabase
              .from('page_sections')
              .insert([{
                page_id: pageId,
                title: section.title,
                content: section.content,
                order: section.order
              }]);
            
            if (insertError) throw insertError;
          }
          
          // Reload sections
          const { data: newSections, error: newSectionsError } = await supabase
            .from('page_sections')
            .select('*')
            .eq('page_id', pageId)
            .order('order', { ascending: true });
          
          if (newSectionsError) throw newSectionsError;
          
          if (newSections) {
            setPageData(prev => ({
              ...prev,
              sections: newSections.map(section => ({
                id: section.id,
                title: section.title,
                content: section.content,
                order: section.order
              }))
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading page data:', error);
      toast.error('Erreur lors du chargement des données de la page');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (index: number, value: string) => {
    const newSections = [...pageData.sections];
    newSections[index].content = value;
    setPageData({ ...pageData, sections: newSections });
  };

  const handleSectionTitleChange = (index: number, value: string) => {
    const newSections = [...pageData.sections];
    newSections[index].title = value;
    setPageData({ ...pageData, sections: newSections });
  };

  const addSection = async () => {
    try {
      const newOrder = pageData.sections.length > 0 
        ? Math.max(...pageData.sections.map(s => s.order)) + 1 
        : 1;
      
      const { data, error } = await supabase
        .from('page_sections')
        .insert([{
          page_id: pageData.id,
          title: 'Nouvelle Section',
          content: '<p>Contenu de la nouvelle section</p>',
          order: newOrder
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      if (data) {
        setPageData({
          ...pageData,
          sections: [...pageData.sections, {
            id: data.id,
            title: data.title,
            content: data.content,
            order: data.order
          }]
        });
        toast.success('Section ajoutée avec succès');
      }
    } catch (error) {
      console.error('Error adding section:', error);
      toast.error('Erreur lors de l\'ajout de la section');
    }
  };

  const removeSection = async (index: number) => {
    try {
      const sectionId = pageData.sections[index].id;
      
      const { error } = await supabase
        .from('page_sections')
        .delete()
        .eq('id', sectionId);
      
      if (error) throw error;
      
      const newSections = [...pageData.sections];
      newSections.splice(index, 1);
      setPageData({ ...pageData, sections: newSections });
      
      toast.success('Section supprimée avec succès');
    } catch (error) {
      console.error('Error removing section:', error);
      toast.error('Erreur lors de la suppression de la section');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Update page data
      const { error: pageError } = await supabase
        .from('pages')
        .update({
          title: pageData.title,
          description: pageData.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', pageData.id);
      
      if (pageError) throw pageError;
      
      // Update each section
      for (const section of pageData.sections) {
        const { error: sectionError } = await supabase
          .from('page_sections')
          .update({
            title: section.title,
            content: section.content
          })
          .eq('id', section.id);
        
        if (sectionError) throw sectionError;
      }
      
      toast.success('Page mise à jour avec succès');
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Erreur lors de l\'enregistrement de la page');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    // Open the page in a new tab
    window.open(`/${page === 'home' ? '' : page}`, '_blank');
  };

  if (loading) {
    return (
      <div className="px-6">
        <PageHeader
          title={`Éditer la Page ${getPageTitle()}`}
          backLink="/admin/dashboard"
          actionLabel="Prévisualiser"
          actionIcon={<Eye size={16} />}
          onActionClick={handlePreview}
        />
        <LoadingState type="form" />
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title={`Éditer la Page ${getPageTitle()}`}
        backLink="/admin/dashboard"
        actionLabel="Prévisualiser"
        actionIcon={<Eye size={16} />}
        onActionClick={handlePreview}
      />
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6 mb-6">
          <ContentCard 
            title={
              <div className="flex items-center">
                {getPageIcon()}
                <span className="ml-2">Informations de la Page</span>
              </div>
            }
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="pageTitle" className="block text-sm font-medium text-gray-700 mb-1">
                  Titre de la page
                </label>
                <input
                  type="text"
                  id="pageTitle"
                  value={pageData.title}
                  onChange={(e) => setPageData({ ...pageData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label htmlFor="pageDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Description (SEO)
                </label>
                <textarea
                  id="pageDescription"
                  value={pageData.description}
                  onChange={(e) => setPageData({ ...pageData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </ContentCard>
          
          {pageData.sections.map((section, index) => (
            <ContentCard
              key={section.id}
              title={
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <Layers size={18} className="mr-2" />
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => handleSectionTitleChange(index, e.target.value)}
                      className="border-none focus:ring-0 p-0 text-base font-medium text-gray-900 bg-transparent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSection(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Supprimer
                  </button>
                </div>
              }
            >
              <div className="space-y-6">
                <div>
                  <label htmlFor={`sectionContent${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                    Contenu
                  </label>
                  <RichTextEditor
                    value={section.content}
                    onChange={(value) => handleContentChange(index, value)}
                  />
                </div>
              </div>
            </ContentCard>
          ))}
          
          <div className="flex justify-center">
            <button
              type="button"
              onClick={addSection}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              + Ajouter une section
            </button>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Enregistrer les modifications
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PageEditor;